import type { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import type { CRUISE_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { trackClientEvent } from 'analytics/analytics'
import { CABIN_CATEGORIES, CABIN_CATEGORIES_RIVER, CruiseType } from 'constants/cruise'

/**
 * Removes the ship name suffix from a cruise title
 * @param title - The cruise title (e.g., "7-Night Caribbean Cruise on Harmony of the Seas")
 * @returns The trimmed title without the ship name (e.g., "7-Night Caribbean Cruise")
 */
export function trimCruiseShipNameFromTitle(title: string): string {
  // Find the last occurrence of " on " and remove it and everything after
  const lastOnIndex = title.toLowerCase().lastIndexOf(' on ')
  if (lastOnIndex === -1) {
    return title
  }
  return title.substring(0, lastOnIndex)
}

export function getCruiseDurationByItinerary(itinerary?: Array<App.CruiseItineraryItem>): number {
  const uniqueItineraryDays = new Set<number>()
  itinerary?.forEach(item => uniqueItineraryDays.add(item.startDay))

  return uniqueItineraryDays.size
}

export function getCruiseCountriesByItinerary(itinerary?: Array<App.CruiseItineraryItem>): Array<string> {
  const countries = (itinerary ?? []).map(({ port }) => port?.countryCode).filter(Boolean) as Array<string>
  return countries?.length ? unique(countries) : []
}

export function getCruisePortsByItinerary(itinerary?: Array<App.CruiseItineraryItem>): Array<string> {
  const cities = (itinerary ?? []).map(({ port }) => {
    if (!port) return

    const portName = port.name.split(',')[0]
    if (port.city?.trim() === '') return portName

    return port.city ?? portName
  }).filter(Boolean) as Array<string>

  return cities?.length ? unique(cities) : []
}

export function offerHasValidDepartures(offer: App.CruiseOffer): boolean {
  return (offer.departureDates ?? []).filter(d => moment(new Date(d)) >= moment()).length > 0
}

type PricingSchedule = Partial<CruisesContract.PricingCancellationSchedule> & Partial<CruisesContract.PricingPaymentSchedule>
export function filterDataByHighestPercentage(data: Array<PricingSchedule>, isNonRefundable: boolean) {
  const areAllDatesEqual = data.every(obj => obj.date === data[0].date)
  let filteredData = data

  if (areAllDatesEqual) {
    filteredData = data.filter(obj => obj.isPercentage)
  }

  const finalResult: Record<string, App.PricingCancellationSchedule> = {}

  filteredData.forEach(schedule => {
    const date = schedule.date
    const amount = schedule.amount
    if (date && amount && (!finalResult[date] || amount > (finalResult[date].amount ?? 0))) {
      finalResult[date] = {
        ...schedule,
        isNonRefundable: !!isNonRefundable,
      } as App.PricingCancellationSchedule
    }
  })

  return Object.values(finalResult)
}

export interface CruiseOfferPriceVariation {
  primaryPrice: {
    total: number
    type: string
    discountPercent?: number
    value?: number
  }
  secondaryPrice?: {
    total: number
    type: string
    discountPercent?: number
    value?: number
  }
}

function getDiscountPercentAndValue(price: App.CruisePrice | App.LowestPriceWithCategory | App.CruisePriceDetails): { discountPercent?: number, value?: number } {
  if ('discountPills' in price && price.discountPills) {
    return {
      discountPercent: price.discountPills.discountPercentage,
      value: price.discountPills.price,
    }
  }

  return {}
}

export function getCruiseOfferPriceVariation(
  offer: App.CruiseOffer,
  price?: App.CruisePrice | App.LowestPriceWithCategory | App.CruisePriceDetails,
  isPricePerNight?: boolean,
): CruiseOfferPriceVariation {
  if (!price) {
    return {
      primaryPrice: {
        total: 0,
        type: 'person',
      },
    }
  }

  const pricePerPerson = {
    total: price.total,
    type: 'person',
    ...getDiscountPercentAndValue(price),
  }

  if (offer.showPricePerNight || isPricePerNight) {
    const pricePerNight = {
      total: Math.round(price.total / offer.duration),
      type: 'night',
    }

    return {
      primaryPrice: pricePerNight,
      secondaryPrice: pricePerPerson,
    }
  }

  return {
    primaryPrice: pricePerPerson,
  }
}

export function getCruiseOfferMemberPriceVariation(
  offer: App.CruiseOffer,
  memberPrice?: number,
  isPricePerNight?: boolean,
): CruiseOfferPriceVariation | undefined {
  if (!memberPrice) {
    return undefined
  }

  const memberPricePerPerson = {
    total: memberPrice,
    type: 'person',
  }

  if (offer.showPricePerNight || isPricePerNight) {
    const memberPricePerNight = {
      total: Math.round(memberPrice / offer.duration),
      type: 'night',
    }

    return {
      primaryPrice: memberPricePerNight,
      secondaryPrice: memberPricePerPerson,
    }
  }

  return {
    primaryPrice: memberPricePerPerson,
  }
}

export interface CruiseOfferPriceVariationNew {
  primaryPrice?: App.Pricing
  secondaryPrice?: App.Pricing
}

export function getCruiseOfferPriceVariationNew(
  offer: App.CruiseOffer,
  price?: App.CruisePrice | App.LowestPriceWithCategory | App.CruisePriceDetails,
  isPricePerNight?: boolean,
): CruiseOfferPriceVariationNew {
  if (!price) {
    return {}
  }

  let pricePerPerson: App.Pricing = {
    price: price.total,
    saleUnit: 'person',
    fees: [],
  }

  if ('discountPills' in price && price.discountPills) {
    pricePerPerson = {
      ...pricePerPerson,
      value: price.discountPills.price,
      discountPercent: price.discountPills.discountPercentage,
    }
  }

  if (offer.showPricePerNight || isPricePerNight) {
    const pricePerNight: App.Pricing = {
      price: Math.round(price.total / offer.duration),
      saleUnit: 'night',
      fees: [],
    }

    return {
      primaryPrice: pricePerNight,
      secondaryPrice: pricePerPerson,
    }
  }

  return {
    primaryPrice: pricePerPerson,
  }
}

export function getItineraryUniqueKey(itinerary: App.CruiseItineraryItem): string {
  const { title, startDay, startTime, endTime } = itinerary
  return `${title}-${startDay}-${startTime}-${endTime}`
}

// ----------------------  Search Utils ----------------------
/**
 * Read cruise line codes from a URL `cruiseLines` param serialized as a comma-separated list
 * (e.g. `?cruiseLines=RCC,CEL`). Returns an empty array when the param is absent.
 */
export function parseCruiseLinesFromUrl(value: string | null | undefined): Array<string> {
  if (!value) return []
  return value.split(',').filter(Boolean)
}

/**
 * Serialize cruise line codes for the URL `cruiseLines` param as a comma-separated list.
 * Returns `undefined` when there are no values, so the param is dropped from the query string.
 */
export function serializeCruiseLinesForUrl(cruiseLines: Array<string> | undefined): string | undefined {
  if (!cruiseLines || cruiseLines.length === 0) return undefined
  return cruiseLines.join(',')
}

function queryParamsToSearchItem(
  queryParams: URLSearchParams,
  idKey: string,
  nameKey: string,
): App.SearchItem | undefined {
  const id = queryParams.get(idKey)
  const name = queryParams.get(nameKey)
  if (id && name) {
    return {
      searchType: 'destination',
      value: id,
      format: {
        mainText: name,
      },
    }
  }
}

function queryParamsToSearchItems(
  queryParams: URLSearchParams,
  idsKey: string,
  namesKey: string,
): Array<App.SearchItem> {
  const searchPlaceIds = queryParams.getAll(idsKey)
  const searchNames = queryParams.getAll(namesKey)

  return searchPlaceIds.map((placeId, index) => {
    return {
      searchType: 'destination',
      value: placeId,
      format: {
        mainText: searchNames[index],
      },
    }
  })
}

export function getCruiseSearchStateFromParams(queryParams: URLSearchParams): Partial<GlobalSearchState> {
  const durationMin = queryParams.get('durationMin')
  const durationMax = queryParams.get('durationMax')
  const durationRange = queryParams.getAll('durationRange') as unknown as Array<CRUISE_FLEXIBLE_DURATION_RANGE>
  return {
    searchItem: queryParamsToSearchItem(queryParams, 'destinationId', 'destinationName'),
    searchItems: queryParamsToSearchItems(queryParams, 'destinationIds', 'destinationNames'),
    secondarySearchItem: queryParamsToSearchItem(queryParams, 'departureId', 'departureName'),
    secondarySearchItems: queryParamsToSearchItems(queryParams, 'departureIds', 'departureNames'),
    cruiseLines: parseCruiseLinesFromUrl(queryParams.get('cruiseLines')),
    flexibleMonths: queryParams.get('departureMonths') ?? undefined,
    durationMin: durationMin ? parseInt(durationMin) : undefined,
    durationMax: durationMax ? parseInt(durationMax) : undefined,
    durationRange: durationRange ?? undefined,
    checkinDate: queryParams.get('checkinDate') ? moment(queryParams.get('checkinDate')) : undefined,
    checkoutDate: queryParams.get('checkoutDate') ? moment(queryParams.get('checkoutDate')) : undefined,
  }
}

export function getCruiseOfferFiltersFromParams(queryParams: URLSearchParams): [App.OfferListFilters, boolean] {
  const destinationIds = queryParams.getAll('destinationIds')
  const destinationId = queryParams.get('destinationId')
  const departureId = queryParams.get('departureId')
  const departureIds = queryParams.getAll('departureIds')
  const minPrice = queryParams.get('minPrice')
  const maxPrice = queryParams.get('maxPrice')
  const durationMin = queryParams.get('durationMin')
  const durationMax = queryParams.get('durationMax')
  const durationRange = queryParams.getAll('durationRange')
  const cruiseShips = queryParams.getAll('cruiseShips')
  const cabinTypes = queryParams.getAll('cabinTypes')
  const isLuxExclusivePromotion = Boolean(queryParams.get('isLuxExclusivePromotion'))
  const isSpecialOffers = Boolean(queryParams.get('isSpecialOffers'))
  const isLuxPlus = Boolean(queryParams.get('isLuxPlus'))
  const hasLuxPlusPrice = Boolean(queryParams.get('hasLuxPlusPrice'))
  const isCruiseTour = Boolean(queryParams.get('isCruiseTour'))
  const offerSource = queryParams.get('offerSource') as App.CruiseOfferSource | undefined
  const cruiseTypes = queryParams.getAll('cruiseTypes')
  const rateCodes = queryParams.getAll('rateCodes')
  const sortBy = queryParams.get('sortBy')
  const priceBy = queryParams.get('priceBy')
  const campaigns = queryParams.getAll('campaigns')
  const checkinDate = queryParams.get('checkinDate')
  const checkoutDate = queryParams.get('checkoutDate')
  const cruiseLines = parseCruiseLinesFromUrl(queryParams.get('cruiseLines'))
  const departureMonths = queryParams.get('departureMonths')

  const filtersApplied = Boolean(
    destinationIds.length ||
    destinationId ||
    departureId ||
    departureIds.length ||
    minPrice ||
    maxPrice ||
    durationMin ||
    durationMax ||
    durationRange.length ||
    cruiseShips.length ||
    cabinTypes.length ||
    isLuxExclusivePromotion ||
    isSpecialOffers ||
    isLuxPlus ||
    hasLuxPlusPrice ||
    isCruiseTour ||
    offerSource ||
    cruiseTypes.length ||
    rateCodes.length ||
    sortBy ||
    priceBy ||
    campaigns.length ||
    checkinDate ||
    checkoutDate ||
    cruiseLines.length ||
    departureMonths,
  )

  return [{
    offerTypes: ['cruise'],
    destinationIds,
    destinationNames: queryParams.getAll('destinationNames'),
    destinationId: destinationId ?? undefined,
    destinationName: queryParams.get('destinationName') ?? undefined,
    departureName: queryParams.get('departureName') ?? undefined,
    departurePlaceId: departureId ?? undefined,
    departureIds,
    departureNames: queryParams.getAll('departureNames'),
    durationMin: durationMin ? parseInt(durationMin) : undefined,
    durationMax: durationMax ? parseInt(durationMax) : undefined,
    durationRange: durationRange ?? undefined,
    departureStartDate: checkinDate ?? undefined,
    departureEndDate: checkoutDate ?? undefined,
    cruiseLines,
    departureMonths: departureMonths?.split(','),
    rateCodes,
    cruiseShips,
    cabinTypes,
    isSpecialOffers,
    isLuxExclusivePromotion,
    isLuxPlus,
    hasLuxPlusPrice,
    isCruiseTour,
    offerSource,
    cruiseTypes,
    minPrice: minPrice ? parseInt(minPrice) : undefined,
    maxPrice: maxPrice ? parseInt(maxPrice) : undefined,
    sortBy: sortBy as App.OfferListSortByOptions ?? undefined,
    priceBy: priceBy ?? undefined,
    campaigns,
  }, filtersApplied]
}

export function getCruiseExtraFilters(queryParams: URLSearchParams, globalSearch?: GlobalSearchState) {
  const cruiseShips = queryParams.getAll('cruiseShips')
  const minPrice = queryParams.get('minPrice')
  const maxPrice = queryParams.get('maxPrice')
  const isSpecialOffers = queryParams.get('isSpecialOffers')
  const isLuxExclusivePromotion = queryParams.get('isLuxExclusivePromotion')
  const isLuxPlus = queryParams.get('isLuxPlus')
  const hasLuxPlusPriceParam = queryParams.get('hasLuxPlusPrice')
  const sortBy = queryParams.get('sortBy')
  const cruiseTypes = queryParams.getAll('cruiseTypes')

  return {
    cruiseShips,
    cabinTypes: queryParams.getAll('cabinTypes'),
    isSpecialOffers: Boolean(isSpecialOffers) || undefined,
    isLuxExclusivePromotion: Boolean(isLuxExclusivePromotion) || undefined,
    isLuxPlus: Boolean(isLuxPlus) || undefined,
    hasLuxPlusPrice: Boolean(hasLuxPlusPriceParam) || undefined,
    sortBy: sortBy || undefined,
    priceBy: queryParams.get('priceBy') ?? undefined,
    minPrice: minPrice ? parseInt(minPrice) : undefined,
    maxPrice: maxPrice ? parseInt(maxPrice) : undefined,
    durationMin: queryParams.get('durationMin') ?? undefined,
    durationMax: queryParams.get('durationMax') ?? undefined,
    durationRange: globalSearch?.durationRange ?? queryParams.getAll('durationRange') ?? undefined,
    campaigns: queryParams.getAll('campaigns') ?? undefined,
    cruiseTypes,
  }
}

export function getCruiseGlobalFilters(globalSearch: GlobalSearchState) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    secondarySearchItems,
    cruiseLines,
    flexibleMonths,
    durationMax,
    durationMin,
    durationRange,
    checkinDate,
    checkoutDate,
  } = globalSearch

  return {
    cruiseLines,
    durationMax: durationMax || undefined,
    durationMin: durationMin || undefined,
    durationRange: durationRange || undefined,
    destinationId: searchItem?.value,
    destinationName: searchItem?.format.mainText,
    departureId: secondarySearchItem?.value,
    departureName: secondarySearchItem?.format.mainText,
    departureMonths: flexibleMonths || undefined,
    destinationNames: searchItems?.map((item) => item.format.mainText),
    destinationIds: searchItems?.map((item) => item.value),
    departureNames: secondarySearchItems?.map((item) => item.format.mainText),
    departureIds: secondarySearchItems?.map((item) => item.value),
    ...(!!checkinDate && !!checkoutDate && {
      checkinDate: checkinDate?.format(ISO_DATE_FORMAT),
      checkoutDate: checkoutDate?.format(ISO_DATE_FORMAT),
      departureStartDate: checkinDate?.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate?.format(ISO_DATE_FORMAT),
    }),
  }
}

export function getGlobalFacetFilters(globalSearch: GlobalSearchState) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    secondarySearchItems,
    cruiseLines,
    flexibleMonths,
    durationMax,
    durationMin,
    checkinDate,
    checkoutDate,
    durationRange,
  } = globalSearch

  const departureMonths = flexibleMonths?.split(',').filter(Boolean) || EmptyArray

  return {
    cruiseLines,
    durationMax: durationMax || undefined,
    durationMin: durationMin || undefined,
    destinationId: searchItem?.value,
    destinationName: searchItem?.format.mainText,
    departureId: secondarySearchItem?.value,
    departureName: secondarySearchItem?.format.mainText,
    destinationNames: searchItems.map((item) => item.format.mainText),
    destinationIds: searchItems.map((item) => item.value),
    departureNames: secondarySearchItems.map((item) => item.format.mainText),
    departureIds: secondarySearchItems.map((item) => item.value),
    ...(departureMonths.length && { departureMonths }),
    ...(!!checkinDate && !!checkoutDate && {
      checkinDate: checkinDate.format(ISO_DATE_FORMAT),
      checkoutDate: checkoutDate.format(ISO_DATE_FORMAT),
      departureStartDate: checkinDate.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate.format(ISO_DATE_FORMAT),
    }),
    ...(durationRange?.length && { durationRange }),
  }
}

export function getCruiseFilters(globalSearch: GlobalSearchState, queryParams?: URLSearchParams) {
  const globalFilters = getCruiseGlobalFilters(globalSearch)

  return {
    ...globalFilters,
    ...(queryParams ? getCruiseExtraFilters(queryParams, globalSearch) : {}),
  }
}

export function getCruiseSearchFiltersBar(
  values: App.CruiseGlobalFilters,
  globalSearch: GlobalSearchState,
  pathname: string,
  regionCode: string,
  forceSearchPage = true,
) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    flexibleMonths,
    durationMax,
    durationMin,
    durationRange,
    cruiseLines,
    checkinDate,
    checkoutDate,
  } = values

  const filters: App.CruiseGlobalFilters = {
    ...globalSearch,
    ...(cruiseLines && { cruiseLines }),
  }

  if ('durationMin' in values) {
    filters.durationMin = durationMin
  }

  if ('durationMax' in values) {
    filters.durationMax = durationMax
  }

  if ('durationRange' in values) {
    filters.durationRange = durationRange
  }

  if ('flexibleMonths' in values) {
    filters.flexibleMonths = flexibleMonths
  }

  if ('searchItem' in values) {
    filters.searchItem = searchItem
  }

  if ('searchItems' in values) {
    filters.searchItems = searchItems
  }

  if ('secondarySearchItem' in values) {
    filters.secondarySearchItem = secondarySearchItem
  }

  if ('checkinDate' in values) {
    filters.checkinDate = checkinDate
  }

  if ('checkoutDate' in values) {
    filters.checkoutDate = checkoutDate
  }

  const pageName = forceSearchPage ? '/search/cruises' : pathname.replace(`/${regionCode.toLowerCase()}`, '')
  return {
    pageName,
    filters: filters as GlobalSearchState,
  }
}

export function resetCruiseTimer() {
  const time = getCruiseVendorBookingTimer()
  // the vendor sends the length of time as seconds thats why we have this "time/60"
  const expirationTimestamp = moment().add(time, 'minutes').toDate().getTime()

  isomorphicSessionStorage.set('cruise-timer-expiration-timestamp', expirationTimestamp)
}

export function expireCruiseTimer() {
  isomorphicSessionStorage.set('cruise-timer-expiration-timestamp', 0)
}

export function setCruiseVendorBookingTimer(time: number) {
  isomorphicSessionStorage.set('cruise-vendor-booking-timer', time)
}

export function getCruiseVendorBookingTimer() {
  return Number(isomorphicSessionStorage.get('cruise-vendor-booking-timer')) / 60
}

export function getCruiseTimerExpirationTimestamp() {
  return Number(isomorphicSessionStorage.get('cruise-timer-expiration-timestamp'))
}

export function countCruiseItemsPassengers(items: Array<App.OrderCruiseItem>): {
  adults: number
  children: number
} {
  const { adults, children } = items.reduce((acc, item) => {
    acc.adults += item.passengers.filter(passenger => passenger.type === 'adult').length
    acc.children += item.passengers.filter(passenger => passenger.type === 'child').length
    return acc
  }, { adults: 0, children: 0 })

  return {
    adults,
    children,
  }
}

export type UniqueItineraryItem = Record<string, {
  item: App.CruiseItineraryItem
  addPortCircle: boolean
  portName: string
  portNumber: number
  isStart: boolean
  isEnd: boolean
  itineraryKey: string
  isSelected: boolean
}>

export function getCruiseUniqueItineraryList(
  itinerary: Array<App.CruiseItineraryItem>,
  showOnlyPorts: boolean,
  portNameSelected?: string | null,
  itineraryKeySelected?: string | null,
) {
  if (!itinerary) return []

  const itineraryWithoutRepeatedPorts: Array<App.CruiseItineraryItem> = Object.values(itinerary.reduce((acc, item) => ({
    ...acc,
    [`${item.startDay}_${item.port?.name || 'at_sea'}`]: item,
  }), {}))

  let portNumber = 0
  let lastPortName = ''

  return Object.values(itineraryWithoutRepeatedPorts.reduce<UniqueItineraryItem>((acc, item, index) => {
    if (showOnlyPorts && !item.port) return acc

    const portName = item.port?.name || 'at_sea'
    const key = `${item.startDay}_${portName}`
    const isStart = index === 0
    const isEnd = index === itineraryWithoutRepeatedPorts.length - 1

    // add port circle if port is different from previous item
    const addPortCircle = !!item.port && lastPortName !== item.port?.name || isStart
    const itineraryKey = getItineraryUniqueKey(item)

    const isSelected = portNameSelected === portName && (!itineraryKeySelected || itineraryKeySelected === itineraryKey)

    const result = {
      ...acc,
      [key]: {
        portName,
        item,
        portNumber,
        addPortCircle,
        isStart,
        isEnd,
        itineraryKey,
        isSelected,
      },
    }

    if (addPortCircle) {
      portNumber += 1
      lastPortName = item.port?.name || ''
    }

    return result
  }, {}))
}

export function trackCruisesClickEvent(subject: string) {
  trackClientEvent({
    subject,
    action: 'clicked',
    category: 'cruise',
    type: 'interaction',
  })
}

export function getCabinCategories(cruiseType?: App.CruiseType) {
  if (cruiseType === CruiseType.RIVER) {
    return CABIN_CATEGORIES_RIVER
  }

  return CABIN_CATEGORIES
}

/** Minutes in a day; used for itinerary duration thresholds (e.g. whole day ashore). */
export const CRUISE_MINUTES_PER_DAY = 24 * 60

const CRUISE_SCHEDULE_MS_PER_MINUTE = 60 * 1000
const CRUISE_SCHEDULE_MS_PER_DAY = 24 * 60 * CRUISE_SCHEDULE_MS_PER_MINUTE
const CRUISE_SCHEDULE_ANCHOR_UTC = Date.UTC(2000, 0, 1)

function parseCruiseScheduleTimeToMinutes(timeStr: string): number | undefined {
  const trimmed = timeStr.trim()
  if (!trimmed || trimmed === '--') {
    return undefined
  }
  const m24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (m24) {
    const h = Number(m24[1])
    const min = Number(m24[2])
    if (h >= 0 && h < 24 && min >= 0 && min < 60) {
      return h * 60 + min
    }
  }
  const parsed = new Date(`January 1, 2000 ${trimmed}`)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getHours() * 60 + parsed.getMinutes()
  }
  return undefined
}

function cruiseDayClockToUtcMs(startDay: number, minutesFromMidnight: number): number {
  return (
    CRUISE_SCHEDULE_ANCHOR_UTC +
    (startDay - 1) * CRUISE_SCHEDULE_MS_PER_DAY +
    minutesFromMidnight * CRUISE_SCHEDULE_MS_PER_MINUTE
  )
}

/** Minutes between arrival and departure clock times for a port day (handles overnight wrap). */
export function getPortMinutesAshore(arrivalRaw: string, departureRaw: string): number | undefined {
  const arrival = parseCruiseScheduleTimeToMinutes(arrivalRaw)
  const departure = parseCruiseScheduleTimeToMinutes(departureRaw)
  if (arrival === undefined || departure === undefined) {
    return undefined
  }
  let diff = departure - arrival
  if (diff < 0) {
    diff += CRUISE_MINUTES_PER_DAY
  }
  return diff
}

/** Elapsed time from previous port departure to next port arrival (full sea segment). */
export function getSeaSegmentMinutes(
  fullItinerary: Array<App.CruiseItineraryItem>,
  seaDayIndex: number,
): number | undefined {
  const row = fullItinerary[seaDayIndex]
  if (row?.port) {
    return undefined
  }

  let prevPortIndex = -1
  for (let i = seaDayIndex - 1; i >= 0; i -= 1) {
    if (fullItinerary[i].port) {
      prevPortIndex = i
      break
    }
  }
  let nextPortIndex = -1
  for (let i = seaDayIndex + 1; i < fullItinerary.length; i += 1) {
    if (fullItinerary[i].port) {
      nextPortIndex = i
      break
    }
  }
  if (prevPortIndex === -1 || nextPortIndex === -1) {
    return undefined
  }

  const prev = fullItinerary[prevPortIndex]
  const next = fullItinerary[nextPortIndex]
  const depM = parseCruiseScheduleTimeToMinutes(prev.endTime)
  const arrM = parseCruiseScheduleTimeToMinutes(next.startTime)
  if (depM === undefined || arrM === undefined) {
    return undefined
  }

  const depMs = cruiseDayClockToUtcMs(prev.startDay, depM)
  const arrMs = cruiseDayClockToUtcMs(next.startDay, arrM)
  const diffMs = arrMs - depMs
  if (diffMs <= 0) {
    return undefined
  }
  return diffMs / CRUISE_SCHEDULE_MS_PER_MINUTE
}
